/**
 * TanStack Query hooks for user blocking. Mutations cascade-invalidate
 * every cached read that the new RLS/RPC will now filter — vehicle lists,
 * profile fetches, conversation lists — so blocked content disappears
 * from every screen automatically (no per-screen logic needed).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { userBlockService, type UserBlockRow } from '../services/user-block.service'

const userBlockKeys = {
  all: ['user-blocks'] as const,
  list: (blockerId: string) => [...userBlockKeys.all, 'list', blockerId] as const,
}

/** Every user the current user has blocked. */
export function useBlockedUsers() {
  const { user } = useAuth()
  return useQuery<UserBlockRow[]>({
    queryKey: userBlockKeys.list(user?.id ?? ''),
    queryFn: () => userBlockService.listBlocked(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 min — blocks change rarely
  })
}

interface BlockMutationInput {
  blockedId: string
  reason?: string
}

/**
 * Block a user. On success, cascade-invalidate every query that the new
 * RLS/RPC will now hide content from — vehicles (nearby + search), the
 * blocked user's profile, the conversation list, and the per-conversation
 * message lists. Removing instead of invalidating ensures the in-flight
 * data isn't briefly visible while the refetch runs.
 */
export function useBlockUser() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ blockedId, reason }: BlockMutationInput) => {
      if (!user?.id) throw new Error('Not authenticated')
      await userBlockService.block(user.id, blockedId, reason)
      return { blockedId }
    },
    onSuccess: ({ blockedId }) => {
      // Drop blocked content from every cache it could be in.
      queryClient.removeQueries({ queryKey: ['vehicles'] })
      queryClient.removeQueries({ queryKey: ['profile', blockedId] })
      queryClient.removeQueries({ queryKey: ['conversations'] })
      queryClient.removeQueries({ queryKey: ['messages'] })
      queryClient.invalidateQueries({ queryKey: userBlockKeys.all })
    },
  })
}

/** Mirror of useBlockUser — unblocks, invalidates the same caches so
 *  previously-hidden content reappears. */
export function useUnblockUser() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ blockedId }: { blockedId: string }) => {
      if (!user?.id) throw new Error('Not authenticated')
      await userBlockService.unblock(user.id, blockedId)
      return { blockedId }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['vehicles'] })
      queryClient.removeQueries({ queryKey: ['profile'] })
      queryClient.removeQueries({ queryKey: ['conversations'] })
      queryClient.removeQueries({ queryKey: ['messages'] })
      queryClient.invalidateQueries({ queryKey: userBlockKeys.all })
    },
  })
}
